import React from "react";
import "./../custom-fonts.css";

/**
 * MotionDrivenZeroGravity
 * PRODUCTION MOBILE–HARDENED EDITION
 *
 * ✔ iOS Safari safe
 * ✔ Android safe
 * ✔ No scroll hijacking
 * ✔ No parallax
 * ✔ Space-grade scroll affordances
 */

export default function MotionDrivenZeroGravity() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="orbital-root">
      <style>{`
/* ======================================================
   ROOT
====================================================== */
.orbital-root {
  background: radial-gradient(
    120% 140% at 50% 20%,
    #0b1b34,
    #02040c 65%
  );
  color: #e6f2ff;
  font-family: Inter, system-ui, -apple-system;
  overflow-x: hidden;
  position: relative;
}

/* ======================================================
   STARFIELD (STATIC, GPU-CHEAP)
====================================================== */
.universe {
  position: absolute;
  inset: 0;
  z-index: 0;
  background:
    radial-gradient(1px 1px at 20% 30%, #ffffff, transparent),
    radial-gradient(1px 1px at 70% 60%, #cce6ff, transparent),
    radial-gradient(1px 1px at 40% 80%, #ffffff, transparent),
    radial-gradient(1px 1px at 90% 20%, #b6ccff, transparent);
  background-size: 700px 700px;
  opacity: 0.35;
  pointer-events: none;
}

/* ======================================================
   HERO (SAFE VIEWPORT)
====================================================== */
.hero {
  position: relative;
  min-height: 100svh;
  padding-bottom: env(safe-area-inset-bottom);
  display: grid;
  place-items: center;
  z-index: 2;
}

.hero-core {
  text-align: center;
  max-width: 900px;
  padding: 2rem;
}

.hero-title {
  font-family: AvianoSansRegular;
  font-size: clamp(3rem, 9vw, 5.2rem);
  line-height: 1.02;
  letter-spacing: -0.02em;

  background: linear-gradient(
    180deg,
    #ffffff,
    #b6dcff 55%,
    #6fbfff
  );
  -webkit-background-clip: text;
  color: transparent;

  text-shadow:
    0 0 28px rgba(120,180,255,0.4),
    0 0 90px rgba(60,120,255,0.25);
}

.hero-sub {
  margin-top: 1.1rem;
  font-family: FFDaxProRegular;
  font-size: 1.1rem;
  opacity: 0.85;
}

/* ======================================================
   SCROLL BUTTONS
====================================================== */
.scroll-actions {
  margin-top: 2.8rem;
  display: flex;
  justify-content: center;
  gap: 1.8rem;
  flex-wrap: wrap;
}

.scroll-button {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  background: none;
  border: none;
  color: inherit;
  padding-bottom: env(safe-area-inset-bottom);
}

.scroll-ring {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 1px solid rgba(140,180,255,0.45);
  display: grid;
  place-items: center;

  box-shadow:
    0 0 18px rgba(140,200,255,0.45),
    inset 0 0 12px rgba(140,200,255,0.25);

  animation: pulse 4s ease-in-out infinite;
}

.scroll-chevron {
  font-size: 1.5rem;
  opacity: 0.9;
}

.scroll-label {
  font-family: FFDaxProRegular;
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  opacity: 0.6;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(0.96);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
}

/* ======================================================
   PHASE SECTIONS
====================================================== */
.phase {
  position: relative;
  min-height: 110svh;
  padding:
    4rem
    1rem
    calc(4rem + env(safe-area-inset-bottom));
  display: grid;
  place-items: center;
  z-index: 2;
}

/* ======================================================
   ORBITAL MODULE
====================================================== */
.orbital-module {
  position: relative;
  max-width: 820px;
  padding: 3.2rem 2.4rem;
  border-radius: 26px;

  background:
    radial-gradient(
      120% 140% at 18% 10%,
      rgba(180,220,255,0.20),
      rgba(20,40,80,0.25) 42%,
      rgba(5,8,20,0.96)
    );

  border: 1px solid rgba(140,180,255,0.14);

  box-shadow:
    0 70px 180px rgba(0,0,0,0.9),
    inset 0 1px 0 rgba(255,255,255,0.08),
    inset 0 -1px 0 rgba(0,0,0,0.7);

  backdrop-filter: blur(22px) saturate(160%);
}

/* ======================================================
   ORBIT RING (DESKTOP ONLY)
====================================================== */
.orbit {
  position: absolute;
  inset: -22%;
  border-radius: 50%;
  border: 1px solid rgba(140,180,255,0.09);
}

/* ======================================================
   TYPOGRAPHY
====================================================== */
.module-tag {
  font-family: FFDaxProRegular;
  font-size: 0.78rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.7;
}

.module-title {
  font-family: FFDaxProRegular;
  font-size: clamp(2.2rem, 6vw, 3.4rem);
  margin: 0.6rem 0 1.1rem;
}

.module-copy {
  font-family: FFDaxProRegular;
  font-size: 1.05rem;
  line-height: 1.7;
  opacity: 0.9;
}

/* ======================================================
   FOOTER
====================================================== */
.footer {
  position: relative;
  z-index: 2;
  padding:
    3rem
    1rem
    calc(3rem + env(safe-area-inset-bottom));
  text-align: center;
  font-family: FFDaxProRegular;
  font-size: 0.7rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.45;

  border-top: 1px solid rgba(140,180,255,0.08);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.04);
}

/* ======================================================
   MOBILE HARDENING
====================================================== */
@media (max-width: 760px) {
  .orbit {
    display: none;
  }

  .orbital-module {
    padding: 2.4rem 1.6rem;
  }

  .scroll-actions {
    gap: 1.4rem;
  }

  .hero-title {
    font-size: clamp(2.4rem, 10vw, 3.4rem);
  }
}
      `}</style>

      <div className="universe" />

      {/* HERO */}
      <section className="hero" id="hero">
        <div className="hero-core">
          <h1 className="hero-title">
            Zero Gravity<br />Therapeutics
          </h1>
          <p className="hero-sub">
            Born in microgravity. Built for Earth.
          </p>

          <div className="scroll-actions">

          <button
              className="scroll-button"
              onClick={() => scrollTo("vision")}
              aria-label="Vision"
            >
              <div className="scroll-ring">
                <div className="scroll-chevron">⌄</div>
              </div>
              <div className="scroll-label">Vision</div>
            </button>

            <button
              className="scroll-button"
              onClick={() => scrollTo("areaofresearch")}
              aria-label="Area of Research"
            >
              <div className="scroll-ring">
                <div className="scroll-chevron">⌄</div>
              </div>
              <div className="scroll-label">Research</div>
            </button>

            <button
              className="scroll-button"
              onClick={() => scrollTo("phase-1")}
              aria-label="R&D"
            >
              <div className="scroll-ring">
                <div className="scroll-chevron">⌄</div>
              </div>
              <div className="scroll-label">R&D Timeline</div>
            </button>

            <button
              className="scroll-button"
              onClick={() => scrollTo("about")}
              aria-label="About"
            >
              <div className="scroll-ring">
                <div className="scroll-chevron">⌄</div>
              </div>
              <div className="scroll-label">About</div>
            </button>
          </div>
        </div>
      </section>

      {/* VISION */}
      <section className="phase" id="vision">
        <div className="orbit" />
        <div className="orbital-module">
          <div className="module-tag">Vision</div>
          <h2 className="module-title">Architected Therapeutics</h2>
          <p className="module-copy">
          Our vision at Zero Gravity Therapeutics is to improve treatment of chronic inflammation and immune dysregulation through better drug delivery. We engineer multiscale hydrogel architectures to enable precise, sustained delivery at the site of disease while reducing systemic side effects.
          </p>
        </div>
      </section>      

      {/* AREA OF RESEARCH */}
      <section className="phase" id="areaofresearch">
        <div className="orbit" />
        <div className="orbital-module">
          <div className="module-tag">Research</div>
          <h2 className="module-title">Agravity Assembly</h2>
          <p className="module-copy">
          Gravity shapes biological and material processes in ways that are rarely explored experimentally. By studying systems where gravity-driven forces are reduced, we investigate assembly behaviors and biological responses that are difficult to observe in conventional laboratory conditions.
          </p>
        </div>
      </section>

      {/* PHASE I */}
      <section className="phase" id="phase-1">
        <div className="orbit" />
        <div className="orbital-module">
          <div className="module-tag">R&D Phase I</div>
          <h2 className="module-title">Microgravity Metamaterial Fabrication Platform</h2>
          <p className="module-copy">
          We are developing the Fractagels™ fabrication platform, a simulated microgravity system that assembles hydrogel precursors into biocompatible statistical metamaterials with tunable, multiscale architectures for therapeutic applications.
          </p>

          <button
            className="scroll-button"
            onClick={() => scrollTo("phase-2")}
          >
            <div className="scroll-ring">
              <div className="scroll-chevron">⌄</div>
            </div>
            <div className="scroll-label">Phase II</div>
          </button>
        </div>
      </section>

      {/* PHASE II */}
      <section className="phase" id="phase-2">
        <div className="orbit" />
        <div className="orbital-module">
          <div className="module-tag">R&D Phase II</div>
          <h2 className="module-title">Fractagels™</h2>
          <p className="module-copy">
          Near-zero gravity reveals how soft matter naturally forms fractal, self-organizing networks when convection and sedimentation are suppressed. Fractagels™ are therapeutic hydrogels engineered under these conditions, enabling multiscale architectures that cannot be produced under normal gravitational constraints.
          </p>

          <button
            className="scroll-button"
            onClick={() => scrollTo("phase-3")}
          >
            <div className="scroll-ring">
              <div className="scroll-chevron">⌄</div>
            </div>
            <div className="scroll-label">Phase III</div>
          </button>
        </div>
      </section>

            {/* PHASE III */}
            <section className="phase" id="phase-3">
        <div className="orbit" />
        <div className="orbital-module">
          <div className="module-tag">R&D Phase III</div>
          <h2 className="module-title">Injectables</h2>
          <p className="module-copy">
          Injectable Fractagels™ are a moonshot research direction to create syringe-deliverable hydrogels that assemble in situ into fractal, multiscale networks for minimally invasive therapies.
          </p>

          <button
            className="scroll-button"
            onClick={() => scrollTo("phase-4")}
          >
            <div className="scroll-ring">
              <div className="scroll-chevron">⌄</div>
            </div>
            <div className="scroll-label">Phase IV</div>
          </button>
        </div>
      </section>

      {/* PHASE IV */}
      <section className="phase" id="phase-4">
        <div className="orbit" />
        <div className="orbital-module">
          <div className="module-tag">R&D Phase IV</div>
          <h2 className="module-title">Space Manufacturing</h2>
          <p className="module-copy">
            Our research roadmap advances from simulated microgravity on Earth
            to true microgravity in space—where manufacturing constraints
            imposed by gravity are entirely removed.
          </p>
        </div>
      </section>

      {/* ABOUT */}
      <section className="phase" id="about">
        <div className="orbit" />
        <div className="orbital-module">
          <div className="module-tag">About</div>
          <h2 className="module-title">Company</h2>
          <p className="module-copy">
            Zero Gravity Therapeutics, Inc is a Space Medicine startup in Palo Alto,
            CA.
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        © {new Date().getFullYear()} Zero Gravity Therapeutics, Inc. All rights reserved.
      </footer>
    </div>
  );
}




